import React, { Fragment, useState } from 'react';
import {
  Datagrid,
  DateField,
  List,
  NumberField,
  ReferenceField,
  TextField,
  TopToolbar,
  Button,
  useListContext,
  useRecordContext,
  useNotify,
  useUpdate,
  useRefresh,
  FunctionField,
} from 'react-admin';
import {
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button as MuiButton,
  CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useShipDayPlan } from './shipDayApi';
import { CremationStatus, Funeral } from '../types';
import { CremationStatusField } from '../components/CremationStatusField';
import { DeliveryAddressesModal } from './DeliveryAddressesModal';
import { ShippingLabelModal } from './ShippingLabelModal';
import { CourrierPlusModal } from './CourrierPlusModal';
import { splitDeliveryNote } from './deliveryInstructions';
import { courierProblem, courierProblemLabel } from './courierStatus';
import {
  ScheduledPickupsBanner,
  ScheduledPickupsProvider,
  SchedulePickupButton,
} from './ScheduledPickups';
import { PickupRunsPanel } from './PickupRuns';
import { parseDateStr } from './pickupSchedule';
import { pickupNeedsDate, PICKUP_TBD_LABEL } from './pickupDateTbd';
import { getToken } from '../authentication/authProvider';
import { crematoriumShortName } from './labelSafety';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { SHIPPING_NOTIFY_RESET } from './labelNotify';

const ClientAddressDisplay = () => {
  const client = useRecordContext();
  const notify = useNotify();
  
  if (!client) return <span></span>;

  // Format address with apartment-civic_number format
  const streetParts = [];
  
  // Combine apartment and civic number with dash if both exist
  if (client.apartment && client.civic_number) {
    streetParts.push(`${client.apartment}-${client.civic_number}`);
  } else if (client.apartment) {
    streetParts.push(client.apartment);
  } else if (client.civic_number) {
    streetParts.push(client.civic_number);
  }
  
  // Add the street address
  if (client.address) {
    streetParts.push(client.address);
  }
  
  const streetAddress = streetParts.join(' ');
  
  const fullAddressParts = [
    streetAddress,
    client.city,
    client.province,
    client.postal_code
  ].filter(Boolean);
  
  const fullAddress = fullAddressParts.join(', ');

  const handleCopy = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    navigator.clipboard.writeText(fullAddress).then(() => {
      notify('Address copied to clipboard', { type: 'info' });
    }).catch(() => {
      notify('Failed to copy address', { type: 'error' });
    });
  };
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        '&:hover .copy-button': {
          opacity: 1
        }
      }}
      onClick={(e) => e.stopPropagation()} // Prevent row click on the entire box
    >
      <span>{fullAddress}</span>
      <Tooltip title="Copy address">
        <IconButton
          size="small"
          onClick={handleCopy}
          className="copy-button"
          sx={{ 
            opacity: 0, 
            transition: 'opacity 0.2s',
            padding: '2px'
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const DeliveryAddressDisplay = () => {
  const funeral = useRecordContext<Funeral>();
  const notify = useNotify();
  
  if (!funeral) return <span></span>;

  const delivery = funeral.delivery_address;
  if (!delivery?.address) return <span>No address</span>;

  // Format the delivery address
  const formatAddress = () => {
    const parts = [];
    
    // Format street address (apartment-civic_number address format)
    const streetParts = [];
    if (delivery.apartment && delivery.civic_number) {
      streetParts.push(`${delivery.apartment}-${delivery.civic_number}`);
    } else if (delivery.apartment) {
      streetParts.push(delivery.apartment);
    } else if (delivery.civic_number) {
      streetParts.push(delivery.civic_number);
    }
    if (delivery.address) {
      streetParts.push(delivery.address);
    }
    if (streetParts.length > 0) {
      parts.push(streetParts.join(' '));
    }
    
    if (delivery.city) parts.push(delivery.city);
    if (delivery.province) parts.push(delivery.province);
    if (delivery.postal_code) parts.push(delivery.postal_code);
    return parts.join(', ');
  };

  const fullAddress = formatAddress();

  const handleCopy = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(fullAddress).then(() => {
      notify('Address copied to clipboard', { type: 'info' });
    }).catch(() => {
      notify('Failed to copy address', { type: 'error' });
    });
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        '&:hover .copy-button': {
          opacity: 1
        }
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box>
        <span>{fullAddress}</span>
        {delivery.recipient_name && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            Attn: {delivery.recipient_name}
          </Typography>
        )}
      </Box>
      <Tooltip title="Copy address">
        <IconButton
          size="small"
          onClick={handleCopy}
          className="copy-button"
          sx={{ 
            opacity: 0, 
            transition: 'opacity 0.2s',
            padding: '2px'
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const ClientPhoneDisplay = () => {
  const client = useRecordContext();
  const notify = useNotify();
  
  if (!client || !client.phone_number) return <span></span>;

  const displayPhone = client.phone_number;
  // Remove +1 prefix for copying
  const copyPhone = client.phone_number.replace(/^\+1\s?/, '');

  const handleCopy = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    navigator.clipboard.writeText(copyPhone).then(() => {
      notify('Phone number copied to clipboard', { type: 'info' });
    }).catch(() => {
      notify('Failed to copy phone number', { type: 'error' });
    });
  };
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        '&:hover .copy-button': {
          opacity: 1
        }
      }}
      onClick={(e) => e.stopPropagation()} // Prevent row click on the entire box
    >
      <span>{displayPhone}</span>
      <Tooltip title="Copy phone number (without +1)">
        <IconButton
          size="small"
          onClick={handleCopy}
          className="copy-button"
          sx={{ 
            opacity: 0, 
            transition: 'opacity 0.2s',
            padding: '2px'
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const DeliveryNoteDisplay = () => {
  const funeral = useRecordContext<Funeral>();

  if (!funeral?.delivery_note) return <span>-</span>;

  // The family's driver instructions (T-107) get their own line, so a buzzer
  // number is never hidden behind the ellipsis of a staff note.
  const { staff, family } = splitDeliveryNote(funeral.delivery_note);
  const oneLine = {
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'help',
  } as const;

  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{funeral.delivery_note}</span>} placement="top">
      <Box>
        {staff && (
          <Typography variant="body2" sx={oneLine}>
            {staff}
          </Typography>
        )}
        {family && (
          <Typography variant="body2" sx={{ ...oneLine, color: 'info.dark' }}>
            <strong>Family:</strong> {family}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const DeliveryDateDisplay = () => {
  const funeral = useRecordContext<Funeral>();
  
  if (!funeral?.delivery_day) return <span>-</span>;
  
  // Parse the date string as local date to avoid timezone issues
  // delivery_day is expected to be in YYYY-MM-DD format
  const [year, month, day] = funeral.delivery_day.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed in JS Date
  
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  
  return <span>{formattedDate}</span>;
};

const DeliveryWindowDisplay = () => {
  const funeral = useRecordContext<Funeral>();
  
  if (!funeral?.delivery_window) return <span>-</span>;
  
  return <span>{funeral.delivery_window}</span>;
};

const ShippingLabelActions = () => {
  const funeral = useRecordContext<Funeral>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voiding, setVoiding] = useState(false);

  if (!funeral) return <span>-</span>;

  // Show icon when:
  // 1. delivery_method is explicitly set to 'shipping', OR
  // 2. No delivery_method set but postal code doesn't start with 'H' (outside Montreal)
  const isShipping = funeral.delivery_method === 'shipping' ||
    (!funeral.delivery_method &&
     funeral.delivery_address?.postal_code &&
     !funeral.delivery_address.postal_code.trim().toUpperCase().startsWith('H'));

  // Don't show anything if not shipping or no address
  if (!isShipping || !funeral.delivery_address?.address) {
    return <span>-</span>;
  }

  const handleVoidLabel = async () => {
    if (!funeral.shipping_shipment_id) {
      notify('No shipment ID found — this label was created before void support was added', { type: 'error' });
      return;
    }

    try {
      setVoiding(true);
      const apiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = await getToken();

      const response = await fetch(
        `${baseUrl}/canada-post/shipment/${funeral.shipping_shipment_id}/refund`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to void label');
      }

      // Clear shipping fields from the funeral record
      await update(
        'funerals',
        {
          id: funeral.id,
          data: {
            shipping_label_url: null,
            shipping_tracking_number: null,
            shipping_shipment_id: null,
            // The voided label's notification goes with it (T-114): a queued
            // text must not go out later with the old tracking number.
            ...SHIPPING_NOTIFY_RESET,
          },
          previousData: funeral,
        },
        {
          onSuccess: () => {
            notify('Shipping label voided — refund requested from Canada Post', { type: 'success' });
            void refresh();
          },
          onError: (error: any) => {
            notify(`Label voided but failed to clear record: ${error.message}`, { type: 'warning' });
          },
        }
      );
    } catch (error) {
      console.error('Error voiding label:', error);
      notify(
        `Error voiding label: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' }
      );
    } finally {
      setVoiding(false);
      setVoidDialogOpen(false);
    }
  };

  // If label already exists, show view icon
  if (funeral.shipping_label_url) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="View Label">
          <IconButton
            size="small"
            onClick={async (e) => {
              e.stopPropagation();
              if (funeral.shipping_label_url) {
                try {
                  const apiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
                  const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
                  const labelProxyUrl = `${baseUrl}/canada-post/label?url=${encodeURIComponent(funeral.shipping_label_url)}`;

                  // Fetch with authentication
                  const token = await getToken();
                  const response = await fetch(labelProxyUrl, {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                    },
                  });

                  if (!response.ok) {
                    throw new Error('Failed to fetch label');
                  }

                  // Create blob and open in new window
                  const blob = await response.blob();
                  const blobUrl = URL.createObjectURL(blob);
                  window.open(blobUrl, '_blank');

                  // Clean up the blob URL after a delay
                  setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
                } catch (error) {
                  console.error('Error fetching label:', error);
                  notify('Error fetching label', { type: 'error' });
                }
              }
            }}
            color="primary"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download 4x6 Label (PDF)">
          <IconButton
            size="small"
            onClick={async (e) => {
              e.stopPropagation();
              if (funeral.shipping_label_url) {
                try {
                  const apiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
                  const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
                  const thermalLabelProxyUrl = `${baseUrl}/canada-post/label/thermal?url=${encodeURIComponent(funeral.shipping_label_url)}`;

                  // Fetch with authentication
                  const token = await getToken();
                  const response = await fetch(thermalLabelProxyUrl, {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                    },
                  });

                  if (!response.ok) {
                    throw new Error('Failed to fetch thermal label');
                  }

                  // The backend now serves a 4x6 PDF (the same page the pickup run
                  // prints); an older backend still answers ZPL as text/plain.
                  const contentType = response.headers.get('Content-Type');
                  const isZPL = contentType?.includes('text/plain');
                  const extension = isZPL ? 'zpl' : 'pdf';

                  // Download file
                  const blob = await response.blob();
                  const blobUrl = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = blobUrl;
                  link.download = `thermal-label-${funeral.shipping_tracking_number || 'label'}.${extension}`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);

                  // Clean up the blob URL
                  setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
                  notify(`4x6 label downloaded ${isZPL ? '(ZPL for thermal printer)' : '(PDF)'}`, { type: 'success' });
                } catch (error) {
                  console.error('Error fetching thermal label:', error);
                  notify('Error downloading thermal label', { type: 'error' });
                }
              }
            }}
            color="secondary"
          >
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Void Label">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setVoidDialogOpen(true);
            }}
            color="error"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {funeral.shipping_tracking_number && (
          <Typography variant="caption" color="text.secondary">
            {funeral.shipping_tracking_number}
          </Typography>
        )}
        <Dialog
          open={voidDialogOpen}
          onClose={() => !voiding && setVoidDialogOpen(false)}
          onClick={(e) => e.stopPropagation()}
        >
          <DialogTitle>Void Shipping Label?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              A refund will be requested from Canada Post. The label and tracking
              number will be removed, and you can create a new label afterward.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={() => setVoidDialogOpen(false)} disabled={voiding}>
              Cancel
            </MuiButton>
            <MuiButton
              onClick={handleVoidLabel}
              color="error"
              variant="contained"
              disabled={voiding}
            >
              {voiding ? (
                <>
                  <CircularProgress size={18} sx={{ mr: 1 }} />
                  Voiding...
                </>
              ) : (
                'Void Label'
              )}
            </MuiButton>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // No label yet — creation now happens by clicking the Status chip.
  return <span>-</span>;
};

// Opens a courier PDF (delivery slip = confirmation, or label) once dispatched.
// Streams through our backend so the request carries the auth token.
const CourierConfirmation = () => {
  const funeral = useRecordContext<Funeral>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [refreshing, setRefreshing] = useState(false);
  if (!funeral?.courier_call_number) return <span>-</span>;
  const problem = courierProblem(funeral.courier_status);

  const apiBase = () =>
    (process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001').replace(/\/$/, '');

  const openDoc = async (kind: 'slip' | 'label') => {
    try {
      const token = await getToken();
      const call = encodeURIComponent(funeral.courier_call_number as string);
      const response = await fetch(`${apiBase()}/courrier-plus/delivery/${call}/${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch document');
      const blob = await response.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (error) {
      notify('Could not fetch courier document', { type: 'error' });
    }
  };

  const refreshStatus = async () => {
    try {
      setRefreshing(true);
      const token = await getToken();
      const response = await fetch(`${apiBase()}/courrier-plus/funeral/${funeral.id}/refresh-status`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to refresh');
      notify('Courier status refreshed', { type: 'info' });
      refresh();
    } catch (error) {
      notify('Could not refresh courier status', { type: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
      <Tooltip title="View courier confirmation (delivery slip)">
        <IconButton size="small" onClick={() => void openDoc('slip')}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Print courier label">
        <IconButton size="small" onClick={() => void openDoc('label')}>
          <PrintIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Refresh status now">
        <span>
          <IconButton size="small" disabled={refreshing} onClick={() => void refreshStatus()}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {problem ? (
        <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
          {courierProblemLabel(problem)} — rebook
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {funeral.courier_status || funeral.courier_call_number}
        </Typography>
      )}
    </Box>
  );
};

// The delivery document for a row: Canada Post label actions (view/print/void)
// for shipping, or the courier confirmation slip for a dispatched courier.
const DeliveryDocument = () => {
  const funeral = useRecordContext<Funeral>();
  const carrier = deliveryCarrier(funeral);
  // A booked pickup transfer is a Courrier Plus call like any other, so its slip
  // and label belong in the same column as the courier lane's.
  const isCourierDoc =
    carrier === 'courier' || (carrier === 'pickup' && !!funeral?.courier_call_number);
  return isCourierDoc ? <CourierConfirmation /> : <ShippingLabelActions />;
};

// Determines the carrier for a delivery. Courrier Plus serves Quebec (H* postal);
// everything else goes Canada Post. Shared by the status + document columns.
// The postal-code guess is a fallback for legacy rows: a case the family (or
// staff) has confirmed always carries an explicit delivery_method.
const deliveryCarrier = (funeral?: Funeral): 'courier' | 'shipping' | 'pickup' | 'none' => {
  if (!funeral) return 'none';
  const postal = funeral.delivery_address?.postal_code?.trim().toUpperCase();
  const isQuebec = !!postal && postal.startsWith('H');
  if (
    funeral.delivery_method === 'office_pickup' ||
    funeral.delivery_method === 'crematorium_pickup' ||
    funeral.delivery_method === 'pickup'
  ) {
    return 'pickup';
  }
  if (
    funeral.delivery_method === 'courier' ||
    (isQuebec && (funeral.delivery_method === 'hand_delivery' || !funeral.delivery_method))
  ) {
    return 'courier';
  }
  if (funeral.delivery_method === 'shipping' || (!funeral.delivery_method && !isQuebec)) {
    return 'shipping';
  }
  return 'none';
};

// A pickup travels in two legs: a courier carries the ashes from the crematorium
// to the pickup location on transfer_day, and the family collects them on
// delivery_day. Until that courier exists, the date that needs action is the
// transfer — filed under the pickup day, a Friday pickup whose courier has to go
// out Tuesday is invisible on Tuesday, and the ashes stay at the crematorium.
// "Booked" is courier_call_number: the transfer is an ordinary Courrier Plus
// call, written by the same booking the courier lane uses, and there is no
// separate record of it. Returns the day that needs booking, or null.
const transferDayNeedingCourier = (funeral?: Funeral): string | null => {
  if (!funeral || deliveryCarrier(funeral) !== 'pickup') return null;
  if (funeral.courier_call_number) return null;
  return funeral.transfer_day || null;
};

// Where that leg ends. Quebec collects at our office; an Ontario crematorium
// pickup is a Southwest location, which is not "the office".
const transferDestination = (funeral: Funeral): string =>
  funeral.delivery_method === 'crematorium_pickup' ? 'pickup location' : 'office';

// "Fri" for the chip, "Friday, Sep 18" for the tooltip — both parsed as local
// dates, so a YYYY-MM-DD never renders as the day before.
const shortWeekday = (dateStr: string): string =>
  parseDateStr(dateStr).toLocaleDateString('en-US', { weekday: 'short' });

const fullDay = (dateStr: string): string =>
  parseDateStr(dateStr).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

// The one entry point to arrange a delivery: a clickable carrier + readiness chip.
// Not done → click to create a Canada Post label / schedule the courier. Done →
// a green indicator; the document itself lives in the Document column.
/**
 * How long we've been waiting, in the unit that matters at that distance.
 * Hours for the first day — that's the window staff are actually watching —
 * then days, because "73h" tells nobody anything.
 */
const waitedFor = (iso?: string | null): string | null => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const hours = Math.max(0, Math.floor((Date.now() - then) / 3600000));
  return hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
};

const RESEND_RESULT: Record<string, string> = {
  sent: 'Invite sent',
  shadowed: 'Test mode — the message went to Slack, not the family',
  deferred: 'Quiet hours — it goes out in the morning',
  skipped: 'Nothing sent — delivery scheduling is off, or the case has moved on',
};

/**
 * Where the family is in the invite, and the button to ask again.
 *
 * The list could already show a delivery date, but never whether anyone had
 * asked for it — a case waiting on an unanswered invite looked exactly like one
 * nobody had touched. The states are: confirmed (and by whom), invited and
 * waiting, deliberately not messaged, or never invited at all.
 */
const InviteStateField = () => {
  const funeral = useRecordContext<Funeral>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [sending, setSending] = useState(false);
  if (!funeral) return <span>-</span>;

  const handleResend = async (e: React.MouseEvent) => {
    e.stopPropagation(); // the row is a link to the case
    try {
      setSending(true);
      const apiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = await getToken();
      const response = await fetch(
        `${baseUrl}/delivery-scheduling/staff/${funeral.id}/resend-invite`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to resend the invite');
      }
      const { result } = await response.json();
      notify(RESEND_RESULT[result] ?? `Invite: ${result}`, {
        type: result === 'sent' ? 'success' : 'info',
      });
      void refresh();
    } catch (error) {
      notify(
        `Could not resend: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' }
      );
    } finally {
      setSending(false);
    }
  };

  if (funeral.delivery_confirmed_at) {
    const byFamily = funeral.delivery_confirmed_by === 'family';
    return (
      <Tooltip
        title={`Confirmed ${new Date(funeral.delivery_confirmed_at).toLocaleString('en-CA')}${
          byFamily ? ' by the family' : ' by staff on their behalf'
        }`}
      >
        <Chip
          size="small"
          label={byFamily ? 'Family confirmed' : 'Confirmed by staff'}
          color={byFamily ? 'success' : 'default'}
          variant={byFamily ? 'filled' : 'outlined'}
        />
      </Tooltip>
    );
  }

  const waited = waitedFor(funeral.delivery_invite_sent_at);
  const label = funeral.delivery_invite_sent_at
    ? `Invite sent · ${waited}`
    : funeral.delivery_invite_suppressed_at
      ? 'Not messaged'
      : 'No invite';
  const tooltip = funeral.delivery_invite_sent_at
    ? `Sent ${new Date(funeral.delivery_invite_sent_at).toLocaleString('en-CA')} — still waiting for an answer`
    : funeral.delivery_invite_suppressed_at
      ? 'Staff chose not to message the family when they set this status'
      : 'The family has never been asked to pick a time';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={tooltip}>
        <Chip size="small" label={label} variant="outlined" />
      </Tooltip>
      <Tooltip title="Send the invite again">
        <span>
          <IconButton size="small" onClick={handleResend} disabled={sending}>
            {sending ? <CircularProgress size={14} /> : <RefreshIcon fontSize="inherit" />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

const DeliveryStatusField = () => {
  const funeral = useRecordContext<Funeral>();
  const [shipModalOpen, setShipModalOpen] = useState(false);
  const [courierModalOpen, setCourierModalOpen] = useState(false);
  if (!funeral) return <span>-</span>;

  const carrier = deliveryCarrier(funeral);
  const hasAddress = !!(
    funeral.delivery_address?.address || funeral.delivery_address?.civic_number
  );

  const chip = (
    icon: React.ReactElement,
    label: string,
    opts: { ready?: boolean; tooltip: string; onClick?: (e: React.MouseEvent) => void },
  ) => (
    <Tooltip title={opts.tooltip}>
      <Chip
        size="small"
        icon={icon}
        label={label}
        color={opts.ready ? 'success' : 'default'}
        variant={opts.ready ? 'filled' : 'outlined'}
        clickable={!!opts.onClick}
        onClick={opts.onClick}
      />
    </Tooltip>
  );

  // courier_status is a timeline: "Booked - 9:12 AM · Assigned - 9:49 AM · …".
  // Show the latest milestone on the chip; full timeline in the tooltip. Shared
  // with a booked pickup transfer, which is the same Courrier Plus call.
  // `withModal`: the courier lane already renders its own CourrierPlusModal;
  // a booked pickup transfer does not, so the rebook chip brings one.
  const dispatchedChip = (withModal = false) => {
    const status = funeral.courier_status || '';
    // A call that came back is the opposite of done: red, and clickable to
    // book the courier again (a new booking replaces the call number).
    const problem = courierProblem(status);
    if (problem) {
      return (
        <>
          <Tooltip
            title={`#${funeral.courier_call_number} — ${status}. The family did NOT receive the remains. Click to rebook the courier, or arrange a pickup.`}
          >
            <Chip
              size="small"
              icon={<TwoWheelerIcon />}
              label={`${courierProblemLabel(problem)} · Rebook`}
              color="error"
              variant="filled"
              clickable
              onClick={(e) => {
                e.stopPropagation();
                setCourierModalOpen(true);
              }}
            />
          </Tooltip>
          {withModal && (
            <CourrierPlusModal
              open={courierModalOpen}
              onClose={() => setCourierModalOpen(false)}
              funeral={funeral}
            />
          )}
        </>
      );
    }
    const latestMilestone = status.split(' · ').pop()?.split(' - ')[0]?.trim() || 'Dispatched';
    return chip(<TwoWheelerIcon />, latestMilestone, {
      ready: true,
      tooltip: `#${funeral.courier_call_number}${status ? ` — ${status}` : ''}`,
    });
  };

  if (carrier === 'courier') {
    const ready = !!funeral.courier_call_number;
    return (
      <>
        {ready
          ? dispatchedChip()
          : chip(<TwoWheelerIcon />, 'Schedule courier', {
              tooltip: hasAddress ? 'Click to schedule the courier' : 'No delivery address yet',
              onClick: hasAddress
                ? (e) => {
                    e.stopPropagation();
                    setCourierModalOpen(true);
                  }
                : undefined,
            })}
        <CourrierPlusModal
          open={courierModalOpen}
          onClose={() => setCourierModalOpen(false)}
          funeral={funeral}
        />
      </>
    );
  }

  if (carrier === 'shipping') {
    const hasLabel = !!funeral.shipping_label_url;
    const rawStatus = funeral.shipping_status || '';
    // Canada Post's pre-transit status ("Electronic information submitted by
    // shipper" = label created, not scanned) is meaningless to staff → relabel.
    const status = /electronic information submitted/i.test(rawStatus) ? 'Label Created' : rawStatus;
    const delivered = /delivered/i.test(status);
    // Once a label exists, show live tracking status if we have it (polled), else
    // "Label ready". Delivered turns the chip green-solid regardless.
    const label = delivered ? 'Delivered' : status || 'Label ready';
    return (
      <>
        {hasLabel
          ? chip(<LocalShippingIcon />, label, {
              ready: true,
              tooltip: funeral.shipping_tracking_number
                ? `Track on Canada Post — ${funeral.shipping_tracking_number}${status ? ` · ${status}` : ''}`
                : `Canada Post · ${status || 'label issued'}`,
              // Click the chip to open Canada Post tracking for this package.
              onClick: funeral.shipping_tracking_number
                ? (e) => {
                    e.stopPropagation();
                    window.open(
                      `https://www.canadapost-postescanada.ca/track-reperage/en/details/${encodeURIComponent(
                        funeral.shipping_tracking_number as string,
                      )}`,
                      '_blank',
                      'noopener',
                    );
                  }
                : undefined,
            })
          : chip(<LocalShippingIcon />, 'Create label', {
              tooltip: hasAddress
                ? `Click to create a Canada Post label${
                    funeral.delivery_day ? ` — ships ${fullDay(funeral.delivery_day)}` : ' — no ship day set'
                  }`
                : 'No delivery address yet',
              onClick: hasAddress
                ? (e) => {
                    e.stopPropagation();
                    setShipModalOpen(true);
                  }
                : undefined,
            })}
        <ShippingLabelModal
          open={shipModalOpen}
          onClose={() => setShipModalOpen(false)}
          funeral={funeral}
        />
      </>
    );
  }

  if (carrier === 'pickup') {
    // Booked: the transfer reads like any other courier run, and the row has
    // moved to the pickup day by then.
    if (funeral.courier_call_number) return dispatchedChip(true);

    const transferDay = transferDayNeedingCourier(funeral);
    if (transferDay) {
      // The leg that needs booking, and the pickup day it has to beat — the row
      // sits under the transfer date, so it has to say when the family is coming.
      const destination = transferDestination(funeral);
      const hasPickupLocation = !!funeral.pickup_location_id;
      const pickupDay = funeral.delivery_day ? ` · pickup ${shortWeekday(funeral.delivery_day)}` : '';
      return (
        <>
          {chip(<TwoWheelerIcon />, `Schedule courier → ${destination}${pickupDay}`, {
            tooltip: hasPickupLocation
              ? `Ashes move to the ${destination} on ${fullDay(transferDay)} — click to book the courier`
              : 'No pickup location set on this case',
            onClick: hasPickupLocation
              ? (e) => {
                  e.stopPropagation();
                  setCourierModalOpen(true);
                }
              : undefined,
          })}
          <CourrierPlusModal
            open={courierModalOpen}
            onClose={() => setCourierModalOpen(false)}
            funeral={funeral}
          />
        </>
      );
    }

    if (!funeral.delivery_day) {
      return chip(<StorefrontIcon />, PICKUP_TBD_LABEL, {
        tooltip: 'Family collects the remains — no day yet. Set it on the case once they know; nothing goes to the family until then.',
      });
    }
    return chip(<StorefrontIcon />, 'Pickup', { tooltip: 'Family collects the remains' });
  }

  return chip(<HelpOutlineIcon />, '—', { tooltip: 'No delivery method set' });
};

const DeliveryListToolBar = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  // Poll every active courier call + Canada Post shipment for status updates.
  const checkForUpdates = async () => {
    try {
      setChecking(true);
      const token = await getToken();
      const base = (process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001').replace(/\/$/, '');
      const headers = { Authorization: `Bearer ${token}` };
      const poll = (path: string) =>
        fetch(`${base}${path}`, { method: 'POST', headers })
          .then((r) => (r.ok ? r.json() : { updated: 0 }))
          .catch(() => ({ updated: 0 }));
      const [courier, cp] = await Promise.all([
        poll('/courrier-plus/poll-active'),
        poll('/canada-post/poll-active'),
      ]);
      const total = (courier.updated || 0) + (cp.updated || 0);
      notify(
        total > 0 ? `Updated ${total} delivery status(es)` : 'No delivery status changes',
        { type: 'info' },
      );
      refresh();
    } catch (error) {
      notify('Could not check for delivery updates', { type: 'error' });
    } finally {
      setChecking(false);
    }
  };

  return (
    <TopToolbar>
      <Button
        label={checking ? 'Checking…' : 'Check for updates'}
        onClick={() => void checkForUpdates()}
        disabled={checking}
        startIcon={<RefreshIcon />}
      />
      <SchedulePickupButton />
      <Button
        label="Delivery Addresses"
        onClick={() => setModalOpen(true)}
        startIcon={<LocalShippingIcon />}
      />
      <DeliveryAddressesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </TopToolbar>
  );
};

/**
 * Where the remains are, as a pill — the thing to check before a label goes
 * into a pickup batch (case 851's Rideau box went out with Lakeshore's).
 * Rendered inside a partners ReferenceField, so the record is the partner.
 */
const CREMATORIUM_PILL_COLORS: { match: RegExp; bg: string; fg: string }[] = [
  { match: /rideau/i, bg: '#e8eaf6', fg: '#283593' },
  { match: /lakeshore/i, bg: '#e0f2f1', fg: '#00695c' },
  { match: /southwest/i, bg: '#fff3e0', fg: '#e65100' },
];

const CrematoriumPill = () => {
  const partner = useRecordContext<{ id: string; name: string }>();
  if (!partner) return null;
  const colors = CREMATORIUM_PILL_COLORS.find((c) => c.match.test(partner.name || ''));
  return (
    <Chip
      size="small"
      label={crematoriumShortName(partner.name)}
      data-testid="crematorium-pill"
      sx={{
        fontWeight: 600,
        bgcolor: colors?.bg ?? 'grey.100',
        color: colors?.fg ?? 'text.primary',
      }}
    />
  );
};

const CrematoriumColumn = ({ label = 'Crematorium' }: { label?: string }) => (
  <ReferenceField
    label={label}
    source="crematorium_partner_id"
    reference="partners"
    link={false}
    emptyText="—"
  >
    <CrematoriumPill />
  </ReferenceField>
);

/**
 * A Canada Post case with no ship day files under no date and joins no label
 * run, so nobody sees it until a family calls (case 851: confirmed Sep 26,
 * found Sep 30). The server now fills the day on confirmation (T-103); anything
 * it could not — a crematorium with no pickup schedule, remains not yet ready,
 * a failed write — collects here, at the top of the page.
 */
const needsShipDay = (funeral: Funeral): boolean =>
  funeral.delivery_method === 'shipping' &&
  !funeral.delivery_day &&
  (funeral.cremation_status === CremationStatus.DELIVERY_SCHEDULED ||
    funeral.cremation_status === CremationStatus.READY_FOR_DELIVERY);

/**
 * What the server's ShipDayService says for a case in "Needs a ship day": the
 * day it will set at the next sweep, or why it can't (no pickup days on the
 * shipping site). Read from the server so this list, the label dialog and the
 * Slack card never disagree (T-204).
 */
const ShipDayPlanField = ({ funeral }: { funeral: Funeral }) => {
  const plan = useShipDayPlan(funeral.id);
  if (!plan) return <Typography variant="body2" color="text.secondary">…</Typography>;
  if (plan.missing) {
    return (
      <Typography variant="body2" color="error" data-testid="ship-day-plan-missing">
        {plan.missing}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" data-testid="ship-day-plan">
      {plan.dayLabel}
      {plan.site ? ` · ${plan.site} pickup` : ''}
    </Typography>
  );
};

const confirmedAgo = (funeral: Funeral): string => {
  if (!funeral.delivery_confirmed_at) return 'Not confirmed';
  const at = new Date(funeral.delivery_confirmed_at);
  const days = Math.floor((Date.now() - at.getTime()) / 86_400_000);
  const when = at.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  const by = funeral.delivery_confirmed_by === 'family' ? 'family' : 'staff';
  return `${when} by ${by}${days >= 1 ? ` · ${days}d ago` : ''}`;
};

const GroupedDatagrid = () => {
  const { data } = useListContext();
  
  if (!data) return null;

  // Group funerals by cremation status (excluding cremation_scheduled)
  const groupedData: Record<CremationStatus, Funeral[]> = {
    [CremationStatus.READY_FOR_DELIVERY]: [],
    [CremationStatus.CREMATION_SCHEDULED]: [],
    [CremationStatus.TRANSPORT_DISPATCHED]: [],
    [CremationStatus.ARRANGEMENT]: [],
    [CremationStatus.DELIVERY_SCHEDULED]: [],
    [CremationStatus.DELIVERED]: [],
  };

  // Group delivery_scheduled funerals by delivery date
  const deliveryDateGroups: Record<string, Funeral[]> = {};

  const shipDayMissing = (data as Funeral[]).filter(needsShipDay);
  const pickupDateMissing = (data as Funeral[]).filter(pickupNeedsDate);

  data.forEach((funeral: Funeral) => {
    const status = funeral.cremation_status as CremationStatus;
    // Shown once, in "Needs a ship day" at the top, not again further down.
    if (needsShipDay(funeral)) return;
    // Likewise "Pickups needing a date".
    if (pickupNeedsDate(funeral)) return;
    
    // For delivery_scheduled, group by delivery_day
    if (status === CremationStatus.DELIVERY_SCHEDULED) {
      // Use delivery_day if available, otherwise fall back to delivery_date
      let deliveryDateKey: string;
      const transferDay = transferDayNeedingCourier(funeral);
      if (transferDay) {
        // A pickup whose courier leg isn't booked files under the day that
        // booking has to happen — see transferDayNeedingCourier.
        deliveryDateKey = new Date(transferDay).toISOString().split('T')[0];
      } else if (funeral.delivery_day) {
        // Normalize to just the date portion (YYYY-MM-DD)
        deliveryDateKey = new Date(funeral.delivery_day).toISOString().split('T')[0];
      } else if (funeral.delivery_date) {
        // Normalize to just the date portion (YYYY-MM-DD)
        deliveryDateKey = typeof funeral.delivery_date === 'string' 
          ? new Date(funeral.delivery_date).toISOString().split('T')[0]
          : new Date(funeral.delivery_date).toISOString().split('T')[0];
      } else {
        deliveryDateKey = 'No Date Set';
      }
      
      if (!deliveryDateGroups[deliveryDateKey]) {
        deliveryDateGroups[deliveryDateKey] = [];
      }
      deliveryDateGroups[deliveryDateKey].push(funeral);
    } else if (groupedData[status]) {
      groupedData[status].push(funeral);
    }
  });

  // Pickups happening today, lifted out of their date group and shown first.
  // A pickup is the one delivery where someone walks in and the ashes have to
  // already be there — it can't be noticed at 4pm inside a list of couriers.
  //
  // Built from the LOCAL date, not toISOString(): after 8pm Montreal time UTC
  // has already rolled over, and today's pickups would quietly vanish from the
  // list for the rest of the evening.
  const now = new Date();
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate()
  ).padStart(2, '0')}`;
  const todaysGroup = deliveryDateGroups[todayKey] ?? [];
  const pickupsToday = todaysGroup.filter((f) => deliveryCarrier(f) === 'pickup');
  if (pickupsToday.length > 0) {
    const rest = todaysGroup.filter((f) => deliveryCarrier(f) !== 'pickup');
    if (rest.length > 0) {
      deliveryDateGroups[todayKey] = rest;
    } else {
      delete deliveryDateGroups[todayKey];
    }
  }

  // Status order - exclude CREMATION_SCHEDULED
  const statusOrder: CremationStatus[] = [
    CremationStatus.READY_FOR_DELIVERY,
  ];

  const statusLabels: Record<CremationStatus, string> = {
    [CremationStatus.READY_FOR_DELIVERY]: 'Ready for Delivery',
    [CremationStatus.CREMATION_SCHEDULED]: 'Cremation Scheduled',
    [CremationStatus.TRANSPORT_DISPATCHED]: 'Transport Dispatched',
    [CremationStatus.ARRANGEMENT]: 'Arrangement',
    [CremationStatus.DELIVERY_SCHEDULED]: 'Delivery Scheduled',
    [CremationStatus.DELIVERED]: 'Delivered',
  };

  // Sort delivery date groups
  const sortedDeliveryDates = Object.keys(deliveryDateGroups).sort((a, b) => {
    if (a === 'No Date Set') return 1;
    if (b === 'No Date Set') return -1;
    return new Date(a).getTime() - new Date(b).getTime();
  });

  return (
    <Fragment>
      {/* Needs a ship day — first, because nothing else on the page will ever
          show these: they sit under no date and in no label run. */}
      {shipDayMissing.length > 0 && (
        <Fragment>
          <Box
            data-testid="needs-ship-day"
            sx={{
              mt: 2,
              mb: 1,
              px: 2,
              py: 1,
              backgroundColor: '#fdecea',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <EventBusyIcon sx={{ color: '#c62828' }} />
            <Typography variant="h6">Needs a ship day</Typography>
            <Chip
              size="small"
              label={shipDayMissing.length}
              sx={{ bgcolor: '#c62828', color: '#fff', fontWeight: 700 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Canada Post cases with no day — set one on the case so the parcel joins a pickup.
            </Typography>
          </Box>
          <Datagrid data={shipDayMissing} rowClick="show" bulkActionButtons={false} resource="funerals">
            <NumberField source="id" label="ID" options={{ useGrouping: false }} />
            <FunctionField
              label="Name"
              render={(record: Funeral) => (
                <ReferenceField source="deceased_client_id" reference="clients" link="show" record={record}>
                  <TextField source="name" />
                </ReferenceField>
              )}
            />
            <CrematoriumColumn />
            <FunctionField label="Status" render={() => <DeliveryStatusField />} />
            <FunctionField label="Confirmed" render={(record: Funeral) => confirmedAgo(record)} />
            <FunctionField label="Ship day" render={(record: Funeral) => <ShipDayPlanField funeral={record} />} />
            <FunctionField label="Delivery Address" render={() => <DeliveryAddressDisplay />} />
            <DateField
              label="Cremation Date"
              source="cremation_date"
              options={{ month: 'short', year: 'numeric', day: 'numeric' }}
            />
            <FunctionField label="Family" render={() => <InviteStateField />} />
          </Datagrid>
        </Fragment>
      )}

      {/* Pickups needing a date (T-175) — saved as pickup before the family
          knew when. Nothing reaches the family until a day is set, so this
          list is the only reminder to ask them. */}
      {pickupDateMissing.length > 0 && (
        <Fragment>
          <Box
            data-testid="pickups-needing-date"
            sx={{
              mt: 2,
              mb: 1,
              px: 2,
              py: 1,
              backgroundColor: '#fff4e5',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <EventBusyIcon sx={{ color: '#e65100' }} />
            <Typography variant="h6">Pickups needing a date</Typography>
            <Chip
              size="small"
              label={pickupDateMissing.length}
              sx={{ bgcolor: '#e65100', color: '#fff', fontWeight: 700 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Ask the family when they’ll come, then set the day on the case.
            </Typography>
          </Box>
          <Datagrid data={pickupDateMissing} rowClick="show" bulkActionButtons={false} resource="funerals">
            <NumberField source="id" label="ID" options={{ useGrouping: false }} />
            <FunctionField
              label="Name"
              render={(record: Funeral) => (
                <ReferenceField source="deceased_client_id" reference="clients" link="show" record={record}>
                  <TextField source="name" />
                </ReferenceField>
              )}
            />
            <CrematoriumColumn />
            <FunctionField label="Status" render={() => <DeliveryStatusField />} />
            <FunctionField
              label="Pickup location"
              render={(record: Funeral) =>
                record.pickup_location_id ? (
                  <ReferenceField source="pickup_location_id" reference="partners" link={false} record={record}>
                    <TextField source="name" />
                  </ReferenceField>
                ) : (
                  <span>—</span>
                )
              }
            />
            <DateField
              label="Cremation Date"
              source="cremation_date"
              options={{ month: 'short', year: 'numeric', day: 'numeric' }}
            />
          </Datagrid>
        </Fragment>
      )}

      {/* Pickups today — first, because someone is coming to the door. */}
      {pickupsToday.length > 0 && (
        <Fragment>
          <Typography
            variant="h6"
            sx={{
              mt: 2,
              mb: 1,
              px: 2,
              py: 1,
              backgroundColor: '#fff4e5',
              borderRadius: 1,
            }}
          >
            Pickups today ({pickupsToday.length})
          </Typography>
          <Datagrid
            data={pickupsToday}
            rowClick="show"
            bulkActionButtons={false}
            resource="funerals"
          >
            <NumberField source="id" label="ID" options={{ useGrouping: false }} />
            <FunctionField
              label="Name"
              render={(record: Funeral) => (
                <div>
                  <ReferenceField
                    source="deceased_client_id"
                    reference="clients"
                    link="show"
                    record={record}
                  >
                    <TextField source="name" />
                  </ReferenceField>
                  <ReferenceField
                    source="primary_client_id"
                    reference="clients"
                    link={false}
                    record={record}
                  >
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      <TextField source="name" />
                    </Typography>
                  </ReferenceField>
                </div>
              )}
            />
            <CrematoriumColumn />
            <ReferenceField
              label="Phone"
              source="primary_client_id"
              reference="clients"
              link={false}
            >
              <ClientPhoneDisplay />
            </ReferenceField>
            <FunctionField
              label="Status"
              render={(record: Funeral) => <DeliveryStatusField />}
            />
            <FunctionField
              label="Family"
              render={(record: Funeral) => <InviteStateField />}
            />
            <FunctionField
              label="Note"
              render={(record: Funeral) => <DeliveryNoteDisplay />}
            />
          </Datagrid>
        </Fragment>
      )}

      {/* Regular status groups */}
      {statusOrder.map((status) => {
        const funerals = groupedData[status];
        if (funerals.length === 0) return null;

        return (
          <Fragment key={status}>
            <Typography 
              variant="h6" 
              sx={{ 
                mt: 2, 
                mb: 1, 
                px: 2, 
                py: 1, 
                backgroundColor: '#f5f5f5',
                borderRadius: 1
              }}
            >
              {statusLabels[status]} ({funerals.length})
            </Typography>
              <Datagrid 
                data={funerals}
                rowClick="show"
                bulkActionButtons={false}
                resource="funerals"
                sx={{
                  '& .RaDatagrid-headerCell:nth-of-type(1)': { width: '80px' }, // ID column
                  '& .RaDatagrid-headerCell:nth-of-type(2)': { width: '200px' }, // Name column
                  '& .RaDatagrid-headerCell:nth-of-type(3)': { width: '300px' }, // Delivery Address column
                  '& .RaDatagrid-headerCell:nth-of-type(4)': { width: '150px' }, // Phone column
                  '& .RaDatagrid-headerCell:nth-of-type(5)': { width: '200px' }, // Delivery Note column
                  '& .RaDatagrid-headerCell:nth-of-type(6)': { width: '120px' }, // Delivery Date column
                  '& .RaDatagrid-headerCell:nth-of-type(7)': { width: '120px' }, // Delivery Window column
                  '& .RaDatagrid-headerCell:nth-of-type(8)': { width: '150px' }, // Shipping Label column
                  '& .RaDatagrid-headerCell:nth-of-type(9)': { width: '140px' }, // Cremation Date column
                  '& .RaDatagrid-headerCell:nth-of-type(10)': { width: '180px' }, // Cremation Status column
                  '& .RaDatagrid-cell:nth-of-type(1)': { width: '80px' },
                  '& .RaDatagrid-cell:nth-of-type(2)': { width: '200px' },
                  '& .RaDatagrid-cell:nth-of-type(3)': { width: '300px' },
                  '& .RaDatagrid-cell:nth-of-type(4)': { width: '150px' },
                  '& .RaDatagrid-cell:nth-of-type(5)': { width: '200px' },
                  '& .RaDatagrid-cell:nth-of-type(6)': { width: '120px' },
                  '& .RaDatagrid-cell:nth-of-type(7)': { width: '120px' },
                  '& .RaDatagrid-cell:nth-of-type(8)': { width: '150px' },
                  '& .RaDatagrid-cell:nth-of-type(9)': { width: '140px' },
                  '& .RaDatagrid-cell:nth-of-type(10)': { width: '180px' },
                }}
            >
              <NumberField source="id" label="ID" options={{ useGrouping: false}} />
              <FunctionField
                label="Name"
                render={(record: Funeral) => (
                  <div>
                    <ReferenceField
                      source="deceased_client_id"
                      reference="clients"
                      link="show"
                      record={record}
                    >
                      <TextField source="name" />
                    </ReferenceField>
                    <ReferenceField
                      source="primary_client_id"
                      reference="clients"
                      link={false}
                      record={record}
                    >
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        <TextField source="name" />
                      </Typography>
                    </ReferenceField>
                  </div>
                )}
              />
              <FunctionField
                label="Status"
                render={(record: Funeral) => <DeliveryStatusField />}
              />
              <CrematoriumColumn label="From (Crematorium)" />
              <FunctionField
                label="Delivery Address"
                render={(record: Funeral) => <DeliveryAddressDisplay />}
              />
              <ReferenceField
                label="Phone"
                source="primary_client_id"
                reference="clients"
                link={false}
              >
                <ClientPhoneDisplay />
              </ReferenceField>
              <FunctionField
                label="Delivery Note"
                render={(record: Funeral) => <DeliveryNoteDisplay />}
              />
              <FunctionField
                label="Delivery Date"
                render={(record: Funeral) => <DeliveryDateDisplay />}
              />
              <FunctionField
                label="Delivery Window"
                render={(record: Funeral) => <DeliveryWindowDisplay />}
              />
              <FunctionField
                label="Document"
                render={(record: Funeral) => <DeliveryDocument />}
              />
              <DateField
                label="Cremation Date"
                source="cremation_date"
                options={{
                  month: 'short',
                  year: 'numeric',
                  day: 'numeric',
                }}
              />
              {/* Last on purpose: the column widths above are pinned by
                  nth-of-type, so inserting anywhere earlier silently shifts
                  every width onto the wrong column. */}
              <FunctionField
                label="Family"
                render={(record: Funeral) => <InviteStateField />}
              />
            </Datagrid>
          </Fragment>
        );
      })}
      
      {/* Delivery date groups for DELIVERY_SCHEDULED status */}
      {sortedDeliveryDates.map((date) => {
        const funerals = deliveryDateGroups[date];
        if (!funerals || funerals.length === 0) return null;

        // Parse the date string as local date to avoid timezone issues
        let formattedDate = 'No Date Set';
        if (date !== 'No Date Set') {
          const [year, month, day] = date.split('-').map(Number);
          const localDate = new Date(year, month - 1, day); // month is 0-indexed in JS Date
          formattedDate = localDate.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          });
        }

        return (
          <Fragment key={`delivery-${date}`}>
            <Typography 
              variant="h6" 
              sx={{ 
                mt: 2, 
                mb: 1, 
                px: 2, 
                py: 1, 
                backgroundColor: '#e3f2fd',
                borderRadius: 1
              }}
            >
              Delivery Scheduled: {formattedDate} ({funerals.length})
            </Typography>
            <Datagrid 
              data={funerals}
              rowClick="show"
              bulkActionButtons={false}
              resource="funerals"
              sx={{
                '& .RaDatagrid-headerCell:nth-of-type(1)': { width: '80px' },
                '& .RaDatagrid-headerCell:nth-of-type(2)': { width: '200px' },
                '& .RaDatagrid-headerCell:nth-of-type(3)': { width: '300px' },
                '& .RaDatagrid-headerCell:nth-of-type(4)': { width: '150px' },
                '& .RaDatagrid-headerCell:nth-of-type(5)': { width: '200px' },
                '& .RaDatagrid-headerCell:nth-of-type(6)': { width: '120px' },
                '& .RaDatagrid-headerCell:nth-of-type(7)': { width: '120px' },
                '& .RaDatagrid-headerCell:nth-of-type(8)': { width: '150px' },
                '& .RaDatagrid-headerCell:nth-of-type(9)': { width: '140px' },
                '& .RaDatagrid-cell:nth-of-type(1)': { width: '80px' },
                '& .RaDatagrid-cell:nth-of-type(2)': { width: '200px' },
                '& .RaDatagrid-cell:nth-of-type(3)': { width: '300px' },
                '& .RaDatagrid-cell:nth-of-type(4)': { width: '150px' },
                '& .RaDatagrid-cell:nth-of-type(5)': { width: '200px' },
                '& .RaDatagrid-cell:nth-of-type(6)': { width: '120px' },
                '& .RaDatagrid-cell:nth-of-type(7)': { width: '120px' },
                '& .RaDatagrid-cell:nth-of-type(8)': { width: '150px' },
                '& .RaDatagrid-cell:nth-of-type(9)': { width: '140px' },
              }}
            >
              <NumberField source="id" label="ID" options={{ useGrouping: false}} />
              <FunctionField
                label="Name"
                render={(record: Funeral) => (
                  <div>
                    <ReferenceField
                      source="deceased_client_id"
                      reference="clients"
                      link="show"
                    >
                      <TextField source="name" />
                    </ReferenceField>
                  </div>
                )}
              />
              <FunctionField
                label="Status"
                render={(record: Funeral) => <DeliveryStatusField />}
              />
              <CrematoriumColumn label="From (Crematorium)" />
              <FunctionField
                label="Delivery Address"
                render={(record: Funeral) => <DeliveryAddressDisplay />}
              />
              <ReferenceField
                label="Phone"
                source="primary_client_id"
                reference="clients"
                link={false}
              >
                <TextField source="phone" />
              </ReferenceField>
              <FunctionField
                label="Delivery Note"
                render={(record: Funeral) => <DeliveryNoteDisplay />}
              />
              <FunctionField
                label="Delivery Date"
                render={(record: Funeral) => <DeliveryDateDisplay />}
              />
              <FunctionField
                label="Delivery Window"
                render={(record: Funeral) => <DeliveryWindowDisplay />}
              />
              <FunctionField
                label="Document"
                render={(record: Funeral) => <DeliveryDocument />}
              />
              <DateField
                label="Cremation Date"
                source="cremation_date"
                options={{
                  month: 'short',
                  year: 'numeric',
                  day: 'numeric',
                }}
              />
              {/* Last on purpose: the column widths above are pinned by
                  nth-of-type, so inserting anywhere earlier silently shifts
                  every width onto the wrong column. */}
              <FunctionField
                label="Family"
                render={(record: Funeral) => <InviteStateField />}
              />
            </Datagrid>
          </Fragment>
        );
      })}
    </Fragment>
  );
};

export const DeliveriesList = () => {
  // Filter for specific cremation statuses - using PostgREST 'in' operator syntax
  const filter = {
    'cremation_status@in': '(cremation_scheduled,delivery_scheduled,ready_for_delivery)'
  };

  return (
    // The toolbar's "Schedule Pickup" button and the banner above the rows share
    // one modal and one copy of the booked pickups, so the provider wraps both.
    <ScheduledPickupsProvider>
      <List
        resource="funerals"
        storeKey="deliveries"
        actions={<DeliveryListToolBar />}
        filter={filter}
        perPage={100}
        sort={{ field: 'cremation_status', order: "ASC"}}
        pagination={false}
      >
        <Fragment>
          <ScheduledPickupsBanner />
          <PickupRunsPanel />
          <GroupedDatagrid />
        </Fragment>
      </List>
    </ScheduledPickupsProvider>
  );
}; 